/**
 * Convert numeric MRI status to human-readable text
 */
export const getMriStatusText = (status: number | undefined): string => {
    switch (status) {
        case 0:
            return 'MR Unknown';
        case 1:
            return 'MR Unsafe';
        case 2:
            return 'MR Conditional';
        case 3:
            return 'MR Safe';
        default:
            return '';
    }
};

const EMPTY_RICH_TEXT_VALUE = '<p></p>';

const normalizeConditionMarkup = (html?: string | null): string => {
    if (typeof html !== 'string') {
        return '';
    }

    const trimmedHtml = html.trim();
    if (!trimmedHtml || trimmedHtml === EMPTY_RICH_TEXT_VALUE) {
        return '';
    }

    return trimmedHtml;
};

const trimLeadingBreaks = (html: string): string =>
    html.replace(/^(?:\s*<br\s*\/?\s*>\s*)+/gi, '').trim();

const trimTrailingBreaks = (html: string): string =>
    html.replace(/(?:\s*<br\s*\/?\s*>\s*)+$/gi, '').trim();

export const appendConditionBlock = (
    existingHtml: string | undefined | null,
    nextBlock: string | undefined | null
): string => {
    const normalizedExisting = trimTrailingBreaks(
        normalizeConditionMarkup(existingHtml)
    );
    const normalizedBlock = trimLeadingBreaks(
        normalizeConditionMarkup(nextBlock)
    );

    if (!normalizedBlock) {
        return normalizedExisting;
    }

    if (!normalizedExisting) {
        return normalizedBlock;
    }

    return `${normalizedExisting}<br>${normalizedBlock}`;
};

/**
 * Remove a div block by ID, properly handling nested divs.
 * Robust to user edits like whitespace changes, attribute reordering, and quote styles.
 */
export const removeConditionBlock = (html: string, itemId: string): string => {
    // Escape special regex characters in the itemId
    const escapedId = itemId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Flexible regex to match the opening div tag with the specific id
    // Handles: extra whitespace, single/double quotes, other attributes before/after
    const openingTagRegex = new RegExp(
        `<div\\s+(?:[^>]*\\s)?id\\s*=\\s*["']?implant-conditions-${escapedId}["']?(?:\\s[^>]*)?>`,
        'i'
    );

    const match = html.match(openingTagRegex);
    if (!match || match.index === undefined) return html;

    const startIndex = match.index;
    const tagEndIndex = startIndex + match[0].length;

    // Find the matching closing </div> tag, handling nested divs
    let depth = 1;
    let i = tagEndIndex;

    while (i < html.length && depth > 0) {
        if (html.substring(i, i + 4).toLowerCase() === '<div') {
            depth++;
            // Skip past the entire opening tag
            const closingBracket = html.indexOf('>', i);
            if (closingBracket !== -1) {
                i = closingBracket + 1;
            } else {
                i += 4;
            }
        } else if (html.substring(i, i + 6).toLowerCase() === '</div>') {
            depth--;
            if (depth === 0) {
                // Found the matching closing tag
                const endIndex = i + 6;
                let result =
                    html.substring(0, startIndex) + html.substring(endIndex);

                // Clean up surrounding whitespace and <br> tags
                result = cleanupSurroundingWhitespace(result);

                return result;
            }
            i += 6;
        } else {
            i++;
        }
    }

    return html;
};

/**
 * Clean up whitespace artifacts after removing a block:
 * - Remove multiple consecutive <br> tags (keep at most one)
 * - Only remove leading/trailing <br> tags if there's no other meaningful content
 * - Preserve user-added content at the start or end
 */
const cleanupSurroundingWhitespace = (html: string): string => {
    let result = html;

    // Replace multiple consecutive <br> tags (with optional whitespace between) with a single <br>
    result = result.replace(/(<br\s*\/?\s*>\s*){2,}/gi, '<br>');

    // Check if there's meaningful content (not just whitespace and <br> tags)
    const strippedContent = result
        .replace(/<br\s*\/?\s*>/gi, '')
        .replace(/\s+/g, '');

    if (strippedContent.length === 0) {
        // No meaningful content - return empty string
        return '';
    }

    // Only remove leading <br> if the content starts with just <br> tags before actual content
    // But preserve if there's actual content (like user text) that might use the <br>
    result = result.replace(/^(\s*<br\s*\/?\s*>\s*)+(?=<div\s)/i, '');

    // Only remove trailing <br> if it's right after a closing </div> with no other content after
    result = result.replace(/(<\/div>\s*)(<br\s*\/?\s*>\s*)+$/i, '$1');

    // Trim whitespace
    result = result.trim();

    return result;
};

/**
 * Build the conditions block HTML for an implant
 */
export const buildConditionBlock = (
    itemId: string,
    implantModel: string | undefined,
    implantManufacturer: string | undefined,
    mriStatus: number | undefined,
    conditionals: string
): string => {
    const mriStatusText = getMriStatusText(mriStatus);
    const header = `<h5><strong>${implantModel || 'Implant'}</strong>${
        implantManufacturer ? ` (${implantManufacturer})` : ''
    }${mriStatusText ? ` - ${mriStatusText}` : ''}</h5>`;
    return `<div id="implant-conditions-${itemId}">${header}${conditionals}</div>`;
};
